import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import React, { useState, useEffect, useCallback } from "react";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";
const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

function bufToBase64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = "";
  bytes.forEach(b => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function base64urlToUint8(s: string): Uint8Array {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}

async function apiFetch(path: string, opts?: RequestInit) {
  return fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
}

// ── TOTP Section ─────────────────────────────────────────────────────────────

export function TOTPSection({ onSuccess }: { onSuccess?: () => void }) {
  const confirm = useConfirm();
  const [status, setStatus]     = useState<{ enabled: boolean; confirmed_at?: string } | null>(null);
  const [qrCode, setQrCode]     = useState("");
  const [secret, setSecret]     = useState("");
  const [code, setCode]         = useState("");
  const [setupStep, setSetupStep] = useState<"idle" | "scanning" | "confirming">("idle");
  const [msg, setMsg]           = useState("");
  const [loading, setLoading]   = useState(false);

  const loadStatus = useCallback(async () => {
    const r = await apiFetch("/api/totp/status");
    if (r.ok) setStatus(await r.json());
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  async function startSetup() {
    setLoading(true);
    setMsg("");
    const r = await apiFetch("/api/totp/setup", { method: "POST" });
    const d = await r.json();
    setLoading(false);
    if (!r.ok) { setMsg(d.detail || "Error"); return; }
    setQrCode(d.qr_code);
    setSecret(d.secret);
    setSetupStep("scanning");
  }

  async function confirmSetup() {
    if (code.length !== 6) { setMsg("Enter the 6-digit code from your app"); return; }
    setLoading(true);
    setMsg("");
    const r = await apiFetch("/api/totp/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
    const d = await r.json();
    setLoading(false);
    if (!r.ok) { setMsg(d.detail || "Invalid code"); return; }
    setSetupStep("idle");
    setCode("");
    setQrCode("");
    await loadStatus();
    setMsg("✓ Authenticator app activated");
    onSuccess?.();
  }

  async function disableTOTP() {
    if (!await confirm("Remove Google Authenticator from your account?", { confirmLabel: "Remove" })) return;
    setLoading(true);
    const r = await apiFetch("/api/totp/", { method: "DELETE" });
    setLoading(false);
    if (r.ok) { await loadStatus(); setMsg("Authenticator removed"); }
  }

  const cardStyle: React.CSSProperties = {
    background: "white", borderRadius: 12, padding: 28, marginBottom: 20,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  };

  return (
    <div style={cardStyle}>
      <LoadingOverlay active={loading} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>Authenticator App (TOTP)</h3>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
            Google Authenticator, Authy, or any TOTP app — zero cost per login
          </p>
        </div>
        <span style={{
          padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
          background: status?.enabled ? "#dcfce7" : "#f1f5f9",
          color: status?.enabled ? "#166534" : "#64748b",
        }}>
          {status?.enabled ? "Active" : "Not set up"}
        </span>
      </div>

      {msg && (
        <div style={{
          background: msg.startsWith("✓") ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${msg.startsWith("✓") ? "#bbf7d0" : "#fecaca"}`,
          borderRadius: 8, padding: "8px 14px", marginBottom: 16, fontSize: 13,
          color: msg.startsWith("✓") ? "#166534" : "#dc2626",
        }}>
          {msg}
        </div>
      )}

      {setupStep === "idle" && !status?.enabled && (
        <button onClick={startSetup} disabled={loading} style={{
          background: "#1e3a8a", color: "white",
          border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14,
          fontWeight: 600, cursor: "pointer",
        }}>
          {loading ? "Loading…" : "Set up Authenticator"}
        </button>
      )}

      {setupStep === "scanning" && (
        <div>
          <p style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
            1. Open your Authenticator app and scan this QR code:
          </p>
          <img src={qrCode} alt="TOTP QR" style={{ width: 200, height: 200, border: "1px solid #e2e8f0", borderRadius: 8 }} />
          <p style={{ fontSize: 13, color: "#475569", margin: "16px 0 4px" }}>
            Or enter this secret manually:
          </p>
          <code style={{
            display: "block", background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: 6, padding: "8px 12px", fontSize: 13, letterSpacing: 2,
            wordBreak: "break-all", marginBottom: 16,
          }}>
            {secret}
          </code>
          <button onClick={() => setSetupStep("confirming")} style={{
            background: "#1e3a8a", color: "white", border: "none", borderRadius: 8,
            padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}>
            I've scanned it →
          </button>
        </div>
      )}

      {setupStep === "confirming" && (
        <div>
          <p style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
            2. Enter the 6-digit code from your Authenticator app to confirm:
          </p>
          <input
            type="text" inputMode="numeric" maxLength={6} value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="123456" autoFocus
            style={{
              padding: "10px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0",
              fontSize: 20, letterSpacing: 6, width: 140, textAlign: "center", outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={confirmSetup} disabled={loading || code.length !== 6} style={{
              background: "#1e3a8a", color: "white",
              border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14,
              fontWeight: 600, cursor: code.length !== 6 || loading ? "not-allowed" : "pointer",
              opacity: code.length !== 6 ? 0.6 : 1,
            }}>
              {loading ? "Confirming…" : "Activate"}
            </button>
            <button onClick={() => { setSetupStep("idle"); setCode(""); }} style={{
              background: "none", border: "1.5px solid #e2e8f0", borderRadius: 8,
              padding: "10px 20px", fontSize: 14, cursor: "pointer", color: "#64748b",
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {status?.enabled && setupStep === "idle" && (
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={startSetup} disabled={loading} style={{
            background: "none", border: `1.5px solid ${NAVY}`, borderRadius: 8,
            padding: "9px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer", color: NAVY,
          }}>
            Re-setup
          </button>
          <button onClick={disableTOTP} disabled={loading} style={{
            background: "none", border: "1.5px solid #fecaca", borderRadius: 8,
            padding: "9px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer", color: "#dc2626",
          }}>
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

// ── Passkeys Section ──────────────────────────────────────────────────────────

export function PasskeysSection({ onSuccess }: { onSuccess?: () => void }) {
  const tz = useTenantTimezone();
  const confirm = useConfirm();
  const [passkeys, setPasskeys] = useState<any[]>([]);
  const [deviceName, setDeviceName] = useState("My Device");
  const [msg, setMsg]     = useState("");
  const [loading, setLoading] = useState(false);

  const loadPasskeys = useCallback(async () => {
    const r = await apiFetch("/api/passkeys/");
    if (r.ok) setPasskeys(await r.json());
  }, []);

  useEffect(() => { loadPasskeys(); }, [loadPasskeys]);

  async function registerPasskey() {
    setLoading(true);
    setMsg("");
    try {
      const beginR = await apiFetch("/api/passkeys/register/begin", { method: "POST" });
      if (!beginR.ok) { const d = await beginR.json(); setMsg(d.detail || "Error"); return; }
      const opts = await beginR.json();

      const publicKey: PublicKeyCredentialCreationOptions = {
        ...opts,
        challenge: base64urlToUint8(opts.challenge),
        user: { ...opts.user, id: base64urlToUint8(opts.user.id) },
        excludeCredentials: (opts.excludeCredentials || []).map((c: any) => ({
          ...c, id: base64urlToUint8(c.id),
        })),
      };

      const credential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential;
      if (!credential) { setMsg("Passkey creation cancelled"); return; }

      const resp = credential.response as AuthenticatorAttestationResponse;
      const credPayload = {
        id: credential.id,
        rawId: bufToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON:  bufToBase64url(resp.clientDataJSON),
          attestationObject: bufToBase64url(resp.attestationObject),
        },
      };

      const finishR = await apiFetch("/api/passkeys/register/finish", {
        method: "POST",
        body: JSON.stringify({ device_name: deviceName, credential: credPayload }),
      });
      if (!finishR.ok) { const d = await finishR.json(); setMsg(d.detail || "Registration failed"); return; }
      await loadPasskeys();
      setMsg("✓ Passkey registered successfully");
      onSuccess?.();
    } catch (e: any) {
      if (e?.name === "NotAllowedError") {
        setMsg("Passkey creation was cancelled.");
      } else {
        setMsg("Error: " + (e?.message || String(e)));
      }
    } finally {
      setLoading(false);
    }
  }

  async function deletePasskey(id: number, name: string) {
    if (!await confirm(`Remove passkey "${name}"?`, { confirmLabel: "Remove" })) return;
    const r = await apiFetch(`/api/passkeys/${id}`, { method: "DELETE" });
    if (r.ok) { await loadPasskeys(); setMsg("Passkey removed"); }
  }

  const cardStyle: React.CSSProperties = {
    background: "white", borderRadius: 12, padding: 28,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  };

  return (
    <div style={cardStyle}>
      <LoadingOverlay active={loading} />
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>Passkeys (Biometric / Security Key)</h3>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
          Face ID, Touch ID, Windows Hello — sign in instantly without a code
        </p>
      </div>

      {msg && (
        <div style={{
          background: msg.startsWith("✓") ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${msg.startsWith("✓") ? "#bbf7d0" : "#fecaca"}`,
          borderRadius: 8, padding: "8px 14px", marginBottom: 16, fontSize: 13,
          color: msg.startsWith("✓") ? "#166534" : "#dc2626",
        }}>
          {msg}
        </div>
      )}

      {passkeys.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {passkeys.map(pk => (
            <div key={pk.id} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 0", borderBottom: "1px solid #f1f5f9",
            }}>
              <div>
                <div style={{ fontWeight: 600, color: NAVY, fontSize: 14 }}>
                  🔑 {pk.device_name || "Passkey"}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                  Added {formatDate(pk.created_at, "short", tz)}
                  {pk.last_used_at && ` · Last used ${formatDate(pk.last_used_at, "short", tz)}`}
                </div>
              </div>
              <AsyncButton onClick={() => deletePasskey(pk.id, pk.device_name)} style={{
                background: "none", border: "1px solid #fecaca", borderRadius: 6,
                padding: "5px 12px", fontSize: 12, color: "#dc2626", cursor: "pointer",
              }}>
                Remove
              </AsyncButton>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="text" value={deviceName} placeholder="Device name"
          onChange={e => setDeviceName(e.target.value)}
          style={{
            padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0",
            fontSize: 14, width: 160, outline: "none",
          }}
        />
        <button onClick={registerPasskey} disabled={loading} style={{
          background: "#1e3a8a", color: "white",
          border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14,
          fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
        }}>
          {loading ? "Registering…" : "Add Passkey"}
        </button>
      </div>
      {!window.PublicKeyCredential && (
        <p style={{ fontSize: 12, color: "#f59e0b", marginTop: 10 }}>
          ⚠ Your browser doesn't support passkeys.
        </p>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SecurityPage() {
  return (
    <div style={{ maxWidth: 640 }}>
      <p style={{ margin: "0 0 24px", color: "#64748b", fontSize: 13 }}>
        Set up zero-cost authentication — no email needed to log in once configured.
      </p>
      <TOTPSection />
      <PasskeysSection />
    </div>
  );
}
